/**
 * AdminEmailTrace — visual front-end for /api/admin/email-trace/{email}.
 *
 * Operator types an email, gets a complete delivery diagnostic:
 *   • User snapshot (signup, tz, opt-in, verified, last email)
 *   • Blockers list (UTC_TIMEZONE, GLOBAL_FLAG_OFF, etc.)
 *   • Global config (NUDGE_EMAILS_ENABLED, RESEND key present?)
 *   • Recent send attempts (nudge_deliveries)
 *   • Provider events (email_events: delivered/opened/clicked)
 *
 * Used to answer "did this tester actually receive any emails?" without
 * SSH'ing into Mongo.
 */
/* eslint-disable */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ArrowLeft, Search, AlertTriangle, CheckCircle2, XCircle,
  Mail, Eye, MousePointerClick, Activity, Settings2, ShieldAlert,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const Pill = ({ children, tone = 'slate' }) => {
  const tones = {
    slate: 'bg-slate-100 text-slate-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    rose: 'bg-rose-100 text-rose-700',
    amber: 'bg-amber-100 text-amber-700',
    sky: 'bg-sky-100 text-sky-700',
    indigo: 'bg-indigo-100 text-indigo-700',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
};

const eventIcon = (type) => {
  if (type === 'delivered') return <Mail className="h-3.5 w-3.5 text-sky-600" />;
  if (type === 'opened') return <Eye className="h-3.5 w-3.5 text-emerald-600" />;
  if (type === 'clicked') return <MousePointerClick className="h-3.5 w-3.5 text-indigo-600" />;
  if (type === 'check_in_completed') return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700" />;
  return <Activity className="h-3.5 w-3.5 text-gray-500" />;
};

const deliveryTone = (status) => {
  if (status === 'sent') return 'emerald';
  if (status === 'failed') return 'rose';
  if (status === 'skipped') return 'amber';
  return 'slate';
};

const AdminEmailTrace = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user && user !== null && user.role !== 'admin') navigate('/dashboard');
  }, [user, navigate]);

  const run = useCallback(async (e) => {
    const target = (e || email || '').trim();
    if (!target) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const { data } = await axios.get(
        `${API}/admin/email-trace/${encodeURIComponent(target)}`,
        { withCredentials: true },
      );
      setData(data);
      setSearchParams({ email: target });
    } catch (err) {
      const s = err?.response?.status;
      if (s === 401) setError('Please sign in as an admin.');
      else if (s === 403) setError('Admin access required.');
      else setError(err?.response?.data?.detail || 'Trace failed.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  // Auto-run when arriving with ?email=...
  useEffect(() => {
    const q = searchParams.get('email');
    if (q && !data && !loading) run(q);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (ev) => {
    ev.preventDefault();
    run(email);
  };

  const u = data?.user;
  const config = data?.config || {};
  const blockers = data?.blockers || [];
  const deliveries = data?.deliveries || [];
  const events = data?.events || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-gray-100 sticky top-0 md:top-16 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center gap-3">
          <Button
            variant="ghost" size="sm"
            onClick={() => navigate('/admin')}
            aria-label="Back to admin"
            data-testid="admin-trace-back"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">Email Trace</h1>
            <p className="text-[11px] text-gray-500">Per-user delivery diagnostic</p>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-24">
        {/* Input */}
        <Card className="border border-gray-100 rounded-2xl shadow-sm mb-4">
          <CardContent className="p-3 sm:p-4">
            <form onSubmit={handleSubmit} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="pl-9 h-10"
                  data-testid="admin-trace-email-input"
                />
              </div>
              <Button type="submit" disabled={loading || !email.trim()} data-testid="admin-trace-submit">
                {loading ? 'Tracing…' : 'Trace'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {error && (
          <div className="rounded-xl border border-rose-100 bg-rose-50 text-rose-700 text-sm p-3 mb-4 flex items-center gap-2" data-testid="admin-trace-error">
            <ShieldAlert className="h-4 w-4" />
            {error}
          </div>
        )}

        {data && (
          <div className="space-y-4" data-testid="admin-trace-result">
            {/* Blockers — most actionable summary */}
            <Card className={`border rounded-2xl shadow-sm ${blockers.length > 0 ? 'border-amber-200 bg-amber-50/30' : 'border-emerald-200 bg-emerald-50/30'}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  {blockers.length > 0
                    ? <><AlertTriangle className="h-4 w-4 text-amber-600" /> Issues detected</>
                    : <><CheckCircle2 className="h-4 w-4 text-emerald-600" /> No blockers detected</>}
                </CardTitle>
                <CardDescription className="text-xs">
                  {blockers.length > 0 ? 'These are the most likely reasons emails are not landing for this user.' : 'Configuration and user state look healthy.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                {blockers.length === 0 ? (
                  <p className="text-xs text-gray-600">Continue investigating the provider events below if a tester still reports missing emails.</p>
                ) : (
                  <ul className="space-y-1.5" data-testid="admin-trace-blockers">
                    {blockers.map((b, i) => (
                      <li key={i} className="text-xs text-gray-800 flex items-start gap-2">
                        <span className="mt-0.5"><XCircle className="h-3.5 w-3.5 text-rose-600" /></span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* User snapshot */}
            <Card className="border border-gray-100 rounded-2xl shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">User snapshot</CardTitle>
                <CardDescription className="text-xs">
                  {u ? `User exists · id ${u.id}` : 'No user with this email in the users collection.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                {!u ? (
                  <p className="text-xs text-gray-500">This email never created an account on this environment.</p>
                ) : (
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                    <div><dt className="text-gray-500 inline">Email:</dt> <dd className="inline font-medium text-gray-900">{u.email}</dd></div>
                    <div><dt className="text-gray-500 inline">Name:</dt> <dd className="inline text-gray-800">{u.name || '—'}</dd></div>
                    <div>
                      <dt className="text-gray-500 inline">Verified:</dt>{' '}
                      <dd className="inline">{u.email_verified ? <Pill tone="emerald">yes</Pill> : <Pill tone="amber">no</Pill>}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 inline">Opt-in:</dt>{' '}
                      <dd className="inline">{u.nudge_emails_opt_in === false ? <Pill tone="rose">opted out</Pill> : <Pill tone="emerald">opted in</Pill>}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 inline">Timezone:</dt>{' '}
                      <dd className="inline">
                        <span className={u.timezone && u.timezone !== 'UTC' ? 'text-gray-800' : 'text-amber-600 font-medium'}>{u.timezone || '—'}</span>
                      </dd>
                    </div>
                    <div><dt className="text-gray-500 inline">Provider:</dt> <dd className="inline text-gray-800">{u.auth_provider || '—'}</dd></div>
                    <div><dt className="text-gray-500 inline">Signed up:</dt> <dd className="inline text-gray-800">{fmtDate(u.created_at)}</dd></div>
                    <div><dt className="text-gray-500 inline">Last email:</dt> <dd className="inline text-gray-800">{u.last_email_sent_at ? `${fmtDate(u.last_email_sent_at)} · ${u.last_email_type || ''}` : '—'}</dd></div>
                  </dl>
                )}
              </CardContent>
            </Card>

            {/* Global config */}
            <Card className="border border-gray-100 rounded-2xl shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Settings2 className="h-4 w-4 text-indigo-600" />
                  Environment config
                </CardTitle>
                <CardDescription className="text-xs">Mail-related env vars for this environment</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs" data-testid="admin-trace-config">
                  <div>
                    <dt className="text-gray-500 inline">NUDGE_EMAILS_ENABLED:</dt>{' '}
                    <dd className="inline">
                      {String(config.NUDGE_EMAILS_ENABLED).toLowerCase() === 'true'
                        ? <Pill tone="emerald">true</Pill>
                        : <Pill tone="rose">{String(config.NUDGE_EMAILS_ENABLED) || 'unset'}</Pill>}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 inline">RESEND_API_KEY:</dt>{' '}
                    <dd className="inline">
                      {config.RESEND_API_KEY_present ? <Pill tone="emerald">present</Pill> : <Pill tone="rose">missing</Pill>}
                    </dd>
                  </div>
                  <div><dt className="text-gray-500 inline">SENDER_EMAIL:</dt> <dd className="inline text-gray-800 font-mono text-[11px]">{config.SENDER_EMAIL || '—'}</dd></div>
                  <div><dt className="text-gray-500 inline">SENDER_NAME:</dt> <dd className="inline text-gray-800">{config.SENDER_NAME || '—'}</dd></div>
                  <div><dt className="text-gray-500 inline">REPLY_TO_EMAIL:</dt> <dd className="inline text-gray-800 font-mono text-[11px]">{config.REPLY_TO_EMAIL || '—'}</dd></div>
                  <div><dt className="text-gray-500 inline">FRONTEND_URL:</dt> <dd className="inline text-gray-800 font-mono text-[11px]">{config.FRONTEND_URL || '—'}</dd></div>
                </dl>
              </CardContent>
            </Card>

            {/* Deliveries */}
            <Card className="border border-gray-100 rounded-2xl shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Send attempts ({deliveries.length})</CardTitle>
                <CardDescription className="text-xs">Each row is what our scheduler/API tried to send for this user.</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                {deliveries.length === 0 ? (
                  <p className="text-xs text-gray-500">No send attempts recorded for this user.</p>
                ) : (
                  <div className="overflow-x-auto" data-testid="admin-trace-deliveries">
                    <table className="w-full text-xs">
                      <thead className="text-gray-500 text-left">
                        <tr>
                          <th className="font-medium py-2 pr-3">When</th>
                          <th className="font-medium py-2 pr-3">Kind</th>
                          <th className="font-medium py-2 pr-3">Status</th>
                          <th className="font-medium py-2 pr-3 hidden md:table-cell">Resend ID</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-700">
                        {deliveries.map((d, i) => (
                          <tr key={i} className="border-t border-gray-50">
                            <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(d.created_at)}</td>
                            <td className="py-2 pr-3"><Pill tone="slate">{d.kind || '—'}</Pill></td>
                            <td className="py-2 pr-3"><Pill tone={deliveryTone(d.status)}>{d.status || '—'}</Pill></td>
                            <td className="py-2 pr-3 hidden md:table-cell text-gray-500 font-mono text-[11px] truncate max-w-[200px]">{d.provider_id || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Events */}
            <Card className="border border-gray-100 rounded-2xl shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Provider events ({events.length})</CardTitle>
                <CardDescription className="text-xs">Confirmed Resend deliveries, opens, clicks, and in-app check-ins.</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                {events.length === 0 ? (
                  <p className="text-xs text-gray-500">No provider events recorded — no email confirmed delivered/opened.</p>
                ) : (
                  <ul className="space-y-1.5" data-testid="admin-trace-events">
                    {events.map((e, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs">
                        <span className="mt-0.5">{eventIcon(e.type)}</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-gray-800">
                            <span className="font-medium">{e.type}</span>
                            {e.state ? <> · <span className="text-gray-600">{e.state}</span></> : null}
                          </p>
                          <p className="text-[10px] text-gray-400">{fmtDate(e.occurred_at)} {e.provider_id ? `· ${e.provider_id}` : ''}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEmailTrace;
